/* ── Admin Catalog Module ── 
   Liner notes: Gestión de la rejilla de programación y assets.
   Estética: Control de señales UHF.
*/

import { showToast, showConfirm } from '../admin-ui-base.js';
import { authHeaders } from '../admin-auth.js';

/**
 * @param {Object} state - Estado global del catálogo (catalog, selectedChannel)
 * @param {Function} renderVideoList - Callback para refrescar la UI de videos
 * @param {Function} fetchMissingDurations - Callback para actualizar duraciones
 */
export function initCatalogActions(state, renderVideoList, fetchMissingDurations) {
  
  const exportChannel = (e, index) => {
    e.stopPropagation();
    const ch = state.catalog[index];
    const data = JSON.stringify({
      num: ch.num,
      name: ch.name,
      color: ch.color,
      sync: ch.sync,
      videos: ch.videos
    }, null, 2);
    const blob = new Blob([data], { type: 'application/json' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `channel-${ch.num}-${ch.name.replace(/[^a-z0-9]/gi, '_').toLowerCase()}.json`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  };

  const importChannel = (e, index) => {
    e.stopPropagation();
    const input = document.createElement('input');
    input.type = 'file';
    input.accept = '.json';
    input.onchange = function() {
      if (!this.files[0]) return;
      const reader = new FileReader();
      reader.onload = function(e) {
        try {
          const data = JSON.parse(e.target.result);
          if (!Array.isArray(data.videos)) throw new Error('Formato inválido: falta array de vídeos');
          showConfirm(
            `¿Importar ${data.videos.length} vídeos a CH ${state.catalog[index].num}? Los vídeos existentes se conservarán.`,
            async () => {
              state.catalog[index].videos.push(...data.videos);
              renderVideoList();
              if (window.saveCatalog) {
                await window.saveCatalog();
              }
              showToast(`${data.videos.length} VÍDEOS IMPORTADOS Y GUARDADOS`);
            },
            'IMPORTAR JSON'
          );
        } catch(err) {
          showToast('JSON INVÁLIDO', true);
        }
      };
      reader.readAsText(this.files[0]);
    };
    input.click();
  };

  const shareChannel = async (e, index) => {
    e.stopPropagation();
    const ch = state.catalog[index];
    if (!ch.is_owner) {
      showToast('SOLO EL PROPIETARIO PUEDE INVITAR', true);
      return;
    }
    const editor = confirm(
      '¿Permiso de EDITOR (puede guardar la parrilla)?\n\nAceptar = Editor\nCancelar = Solo lectura'
    );
    const role = editor ? 'editor' : 'viewer';
    try {
      const res = await fetch(`/channels/${ch.id}/invite`, {
        method: 'POST',
        headers: { ...authHeaders(), 'Content-Type': 'application/json' },
        body: JSON.stringify({ role }),
      });
      if (!res.ok) throw new Error(await res.text());
      const data = await res.json();
      const url = `${window.location.origin}/admin.html?invite=${data.token}`;
      await navigator.clipboard.writeText(url);
      showToast(role === 'editor' ? 'ENLACE EDITOR COPIADO' : 'ENLACE LECTURA COPIADO');
    } catch (err) {
      showToast('ERROR AL GENERAR ENLACE', true);
      console.error(err);
    }
  };

  const syncMinioTags = async (tags) => {
    if (!tags) { showToast('ESCRIBE ALGÚN TAG', true); return; }
    
    showToast(`BUSCANDO CON TAGS: ${tags}...`);
    try {
      const res = await fetch('/minio/tagged-files', {
        method: 'POST',
        headers: { ...authHeaders(), 'Content-Type': 'application/json' },
        body: JSON.stringify({ tags: tags.split(',').map(t => t.trim()).filter(t => t) })
      });
      if (!res.ok) throw new Error(await res.text());
      const data = await res.json();
      
      if (data.files.length === 0) { showToast('SIN RESULTADOS', true); return; }
      
      const ch = state.catalog[state.selectedChannel];
      const existingUrls = new Set((ch.videos || []).map(v => v.url).filter(Boolean));
      let added = 0;
      
      data.files.forEach(file => {
        if (existingUrls.has(file.url)) return;
        const nameWithoutExt = file.name.replace(/\.[^.]+$/, '');
        ch.videos.push({ type: 'minio', url: file.url, title: nameWithoutExt, duration: 0 });
        added++;
      });
      
      renderVideoList();
      if (added > 0) {
        showToast(`${added} VÍDEOS AÑADIDOS — obteniendo duraciones...`);
        fetchMissingDurations(true); 
      } else {
        showToast('TODO YA ESTABA EN EL CANAL');
      }
    } catch(e) {
      showToast('ERROR AL SINCRONIZAR TAGS', true);
      console.error(e);
    }
  };

  const saveCatalog = async () => {
    const ch = state.catalog[state.selectedChannel];
    if (!ch) return;
    if (ch.can_edit === false) {
      showToast('CANAL EN SOLO LECTURA', true);
      return;
    }

    try {
      const res = await fetch(`/channels/${ch.id}`, {
        method: 'PUT',
        headers: { ...authHeaders(), 'Content-Type': 'application/json' },
        body: JSON.stringify(ch)
      });
      if (!res.ok) {
        const raw = await res.text();
        let detail = raw;
        try {
          const j = JSON.parse(raw);
          detail = typeof j.detail === 'string' ? j.detail : raw;
        } catch (_) {}
        showToast(String(detail).slice(0, 80).toUpperCase(), true);
        return;
      }
      showToast('GUARDADO');
    } catch(err) {
      showToast('ERROR AL GUARDAR', true);
      console.error(err);
    }
  };

  // Retornamos las funciones para que el controlador principal las asigne a la UI
  return {
    exportChannel,
    importChannel,
    shareChannel,
    syncMinioTags,
    saveCatalog
  };
}
